import type { ListPaginationInterface } from '@/interfaces/base/pagination/listPagination.interface';
import {
  cloneColumnsState,
  cloneTableRows,
  cloneTableState,
  getDefaultTableState,
} from '@/modules/pipeline/stores/board/PipelineBoard.normalizers';
import type {
  PipelineBoardCacheEntry,
  PipelineBoardMoveSnapshot,
  PipelineBoardTableRowView,
  PipelineDealCardViewRecord,
} from '@/modules/pipeline/types/Pipeline.types';

export const withStagePageLoading = (
  entry: PipelineBoardCacheEntry,
  payload: {
    stageId: string;
    isLoading: boolean;
  },
): PipelineBoardCacheEntry => {
  return {
    ...entry,
    columns: entry.columns.map((column) => {
      if (column.id !== payload.stageId) {
        return {
          ...column,
          cards: column.cards.map((card) => ({ ...card })),
        };
      }

      return {
        ...column,
        cards: column.cards.map((card) => ({ ...card })),
        isLoadingMore: payload.isLoading,
      };
    }),
  };
};

export const withAppendedStagePage = (
  entry: PipelineBoardCacheEntry,
  payload: {
    stageId: string;
    deals: Array<PipelineDealCardViewRecord>;
    page: number;
    hasMore?: boolean;
  },
): PipelineBoardCacheEntry => {
  const loadedPages = entry.loadedPagesByStageId[payload.stageId] ?? [];

  if (loadedPages.includes(payload.page)) {
    return withStagePageLoading(entry, {
      stageId: payload.stageId,
      isLoading: false,
    });
  }

  const nextLoadedPagesByStageId = {
    ...entry.loadedPagesByStageId,
    [payload.stageId]: [...loadedPages, payload.page].sort(
      (left, right) => left - right,
    ),
  };
  const nextHasMoreByStageId = {
    ...entry.hasMoreByStageId,
    [payload.stageId]: payload.hasMore ?? false,
  };

  return {
    ...entry,
    columns: entry.columns.map((column) => {
      const cards = column.cards.map((card) => ({ ...card }));

      if (column.id !== payload.stageId) {
        return {
          ...column,
          cards,
        };
      }

      const existingDealIds = new Set(cards.map((card) => card.id));
      const nextCards = [
        ...cards,
        ...payload.deals
          .filter((deal) => !existingDealIds.has(deal.id))
          .map((deal) => ({ ...deal })),
      ];

      return {
        ...column,
        cards: nextCards,
        hasMore: nextHasMoreByStageId[payload.stageId] ?? column.hasMore,
        isLoadingMore: false,
      };
    }),
    loadedPagesByStageId: nextLoadedPagesByStageId,
    hasMoreByStageId: nextHasMoreByStageId,
  };
};

export const withTableLoading = (
  entry: PipelineBoardCacheEntry,
  isLoading: boolean,
): PipelineBoardCacheEntry => {
  return {
    ...entry,
    tableState: {
      ...cloneTableState(entry.tableState),
      isLoading,
    },
  };
};

export const withTablePage = (
  entry: PipelineBoardCacheEntry,
  payload: {
    rows: Array<PipelineBoardTableRowView>;
    pagination: ListPaginationInterface;
    isLoading?: boolean;
  },
): PipelineBoardCacheEntry => {
  return {
    ...entry,
    tableState: {
      rows: cloneTableRows(payload.rows),
      pagination: {
        ...payload.pagination,
      },
      isLoading: payload.isLoading ?? false,
    },
  };
};

export const withTablePageFailure = (
  entry: PipelineBoardCacheEntry,
  clearIfEmpty = false,
): PipelineBoardCacheEntry => {
  if (clearIfEmpty) {
    return {
      ...entry,
      tableState: getDefaultTableState(),
    };
  }

  return withTableLoading(entry, false);
};

export const applyOptimisticDealMoveToEntry = (
  entry: PipelineBoardCacheEntry,
  payload: {
    pipelineId: string;
    dealId: string;
    sourceStageId: string;
    destinationStageId: string;
  },
): {
  nextEntry: PipelineBoardCacheEntry;
  snapshot: PipelineBoardMoveSnapshot;
} => {
  const snapshot: PipelineBoardMoveSnapshot = {
    pipelineId: payload.pipelineId,
    columns: cloneColumnsState(entry.columns),
    tableState: cloneTableState(entry.tableState),
  };
  const nextColumns = cloneColumnsState(entry.columns);
  const sourceColumn = nextColumns.find(
    (column) => column.id === payload.sourceStageId,
  );
  const destinationColumn = nextColumns.find(
    (column) => column.id === payload.destinationStageId,
  );

  if (!sourceColumn || !destinationColumn) {
    return {
      nextEntry: entry,
      snapshot,
    };
  }

  const movedDeal = sourceColumn.cards.find(
    (card) => card.id === payload.dealId,
  );

  if (!movedDeal) {
    return {
      nextEntry: entry,
      snapshot,
    };
  }

  sourceColumn.cards = sourceColumn.cards.filter(
    (card) => card.id !== payload.dealId,
  );
  destinationColumn.cards = [{ ...movedDeal }, ...destinationColumn.cards];

  sourceColumn.totalItems = Math.max(0, sourceColumn.totalItems - 1);
  destinationColumn.totalItems += 1;
  sourceColumn.totalAmount = Math.max(
    0,
    sourceColumn.totalAmount - movedDeal.operationValue,
  );
  destinationColumn.totalAmount += movedDeal.operationValue;

  return {
    snapshot,
    nextEntry: {
      ...entry,
      columns: nextColumns,
      tableState: {
        ...cloneTableState(entry.tableState),
        rows: entry.tableState.rows.map((row) => {
          if (row.id !== payload.dealId) {
            return { ...row };
          }

          return {
            ...row,
            stageId: destinationColumn.id,
            stageName: destinationColumn.name,
            stageColor: destinationColumn.color,
            stageProbability: destinationColumn.probability,
          };
        }),
      },
    },
  };
};

export const rollbackOptimisticDealMoveEntry = (
  entry: PipelineBoardCacheEntry,
  snapshot: PipelineBoardMoveSnapshot,
): PipelineBoardCacheEntry => {
  return {
    ...entry,
    columns: cloneColumnsState(snapshot.columns),
    tableState: cloneTableState(snapshot.tableState),
  };
};
